import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import {
  Home, Package, Receipt, Calculator, Trophy, Users, Library, Handshake,
  Settings, MessageSquare, ChevronLeft, ChevronRight, Menu, X, TrendingUp, HelpCircle, Sparkles, Phone, Gift, Lock, ShoppingBag, Download, Share, Store, Bell
} from 'lucide-react';
import { canInstall, promptInstall, isStandalone, isIos } from '../utils/installPrompt';
import WhatsNew from './WhatsNew';
import AccountSwitcher from './AccountSwitcher';
import { useShippingCount } from '../ShippingCountContext';
import { useGradeRadar } from '../GradeRadarContext';

// Lowest tier that unlocks each gated page. Free pages omit this. Used to
// show a subtle lock on the nav so free users discover what upgrading unlocks
// (clicking still lands on the page's own paywall).
const TIER_RANK = { free: 0, starter: 1, dealer: 2, pro: 3, business: 4 };

const BOOK_CALL_URL = 'https://calendly.com/zfinkel1/scn-dealer-demo';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';

// LIVE: Pro Support affiliate link is real (?sca_ref=...). BCW gets added to the
// page as a second vendor once its affiliate app is approved.
const SUPPLIES_LIVE = true;

const NAV_ITEMS = [
  { path: '/', label: 'Dashboard', icon: Home, tour: 'dashboard' },
  { path: '/inventory', label: 'Inventory', icon: Package, tour: 'inventory' },
  { path: '/settings?tab=integrations', label: 'Sales Channels', icon: Store, tour: 'channels', requiredPermission: 'manageChannels' },
  { path: '/orders', label: 'Orders', icon: Receipt, tour: 'orders' },
  { path: '/accounting', label: 'Accounting', icon: Calculator, tour: 'accounting', requiredTier: 'dealer', requiredPermission: 'viewFinancials' },
  { path: '/grading', label: 'Grading', icon: Trophy, tour: 'grading', requiredTier: 'dealer' },
  { path: '/consignment', label: 'Consignment', icon: Handshake, tour: 'consignment', requiredTier: 'dealer' },
  { path: '/customers', label: 'Customers', icon: Users, tour: 'customers', requiredTier: 'starter' },
  { path: '/collections', label: 'Collection', icon: Library, tour: 'collection' },
  { path: '/deal-alerts', label: 'Card Drop Alerts', icon: Bell, tour: 'dealalerts' },
  // Lots moved into Add Inventory ("Create a Lot"), it bundles existing cards,
  // so it lives where you add/build inventory, not as its own nav row.
  { path: '/repricing', label: 'Repricing', icon: TrendingUp, tour: 'repricing', requiredTier: 'dealer', requiredPermission: 'changePricing' },
  // Real pages live in the SCROLLABLE nav, the pinned bottom block was eating
  // 9 rows of screen while actual pages scrolled out of view.
  ...(SUPPLIES_LIVE ? [{ path: '/supplies', label: 'Supplies', icon: ShoppingBag, tour: 'supplies' }] : []),
  { path: '/referral', label: 'Refer & Earn', icon: Gift, tour: 'referral' },
];

// Pinned bottom: ONLY Settings gets a full row. Everything else utility-grade
// (Report Bug / Book a Call / Get the App / What's New / Replay Tour) renders
// as one compact icon row, see the util row in sidebarContent.
const BOTTOM_ITEMS = [
  { path: '/settings', label: 'Settings', icon: Settings, tour: 'settings' },
];

function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  const path = location.pathname;
  const [collapsed, setCollapsed] = useState(true); // start as an icon rail; expands on hover
  const [pinned, setPinned] = useState(false);      // chevron pins it open
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tier, setTier] = useState(null);
  // Effective access rank for the nav locks: trial unlocks everything; a
  // payment-failing paid account is treated as free (matches the API gate).
  const [unlockRank, setUnlockRank] = useState(0);
  // PWA install: a persistent entry point so install isn't only the dismissable
  // banner. Hidden once already installed (standalone).
  const [showInstallHelp, setShowInstallHelp] = useState(false);
  const standalone = isStandalone();

  // Fire the native prompt if it's available; otherwise (iOS / browsers without
  // it) open the manual instructions modal.
  const handleGetApp = async () => {
    if (canInstall()) { await promptInstall(); return; }
    setMobileOpen(false);
    setShowInstallHelp(true);
  };

  useEffect(() => {
    axios.get(`${API}/stripe/subscription`)
      .then(r => {
        const d = r.data || {};
        setTier(d.tier);
        setUnlockRank(d.trialActive ? 99 : (d.paymentFailing ? 0 : (TIER_RANK[d.tier] ?? 0)));
      })
      .catch(() => {});
  }, []);

  const showUpgrade = tier === 'free';

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Bottom-tab "More" button dispatches an event we listen for
  useEffect(() => {
    const open = () => setMobileOpen(true);
    window.addEventListener('scn-open-mobile-menu', open);
    return () => window.removeEventListener('scn-open-mobile-menu', open);
  }, []);

  // Prevent body scroll when mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const { needsShipping, pendingOffers } = useShippingCount();
  const { badgeCount: gradeCount } = useGradeRadar();

  // Reset all per-page tour flags so the first visit to each page auto-fires
  // its tour again. Replaces the old "Replay Tour" flow that drove the global
  // Tour.jsx / MobileTour.jsx, both are gone now.
  const replayTour = () => {
    try {
      let prefix = '';
      const token = localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        prefix = `-${payload.userId || payload.id || ''}`;
      }
      // PageTour: one flag per tour key. Wipe all of them.
      const tourKeys = ['dashboard', 'inventory', 'addinventory', 'editcard', 'channels', 'orders', 'accounting', 'grading', 'consignment', 'collections', 'repricing', 'customers', 'settings'];
      for (const k of tourKeys) {
        localStorage.removeItem(`scn-pagetour-seen-${k}`);
        localStorage.removeItem(`scn-pagetour-seen-${k}${prefix}`);
      }
      // Stale flags from the previous global tour, defensive cleanup so a
      // returning user with old localStorage doesn't carry dead state.
      localStorage.removeItem('scn-tour-v2-completed');
      localStorage.removeItem(`scn-tour-v2-completed${prefix}`);
      localStorage.removeItem('scn-mobile-tour-completed');
      localStorage.removeItem(`scn-mobile-tour-completed${prefix}`);
    } catch {}
    // SPA-navigate rather than the old hard reload, PageTour listens for
    // the `scn-replay-tour` event below and starts whichever per-page tour
    // matches the current route. Avoids the ~1.5s blank flash of a full
    // reload on every Replay click.
    const TOUR_HOME = '/inventory';
    if (location.pathname !== TOUR_HOME) {
      navigate(TOUR_HOME);
      // Wait a tick for PageTour to re-derive its tourKey for the new route
      // before firing the event, otherwise it sees the old tourKey and
      // ignores the dispatch.
      setTimeout(() => window.dispatchEvent(new Event('scn-replay-tour')), 50);
    } else {
      window.dispatchEvent(new Event('scn-replay-tour'));
    }
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase()
    : user?.email?.charAt(0).toUpperCase() || '?';

  const displayName = user?.name || user?.email?.split('@')[0] || 'User';

  const isActive = (itemPath) => {
    if (itemPath === '/') return path === '/';
    // Query-bearing items (Channels → /settings?tab=integrations) match the full URL,
    // and plain /settings shouldn't light up while Channels owns the integrations tab.
    if (itemPath.includes('?')) return (path + location.search) === itemPath;
    if (itemPath === '/settings' && location.search.includes('tab=integrations')) return false;
    return path.startsWith(itemPath);
  };

  const sidebarContent = (isMobile = false) => (
    <>
      {/* Logo */}
      <div className="sidebar-header">
        <Link to="/" className="sidebar-logo" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 11 }}>
          {/* Crisp SVG card mark (matches the favicon / app icon) + wordmark. */}
          <svg width="36" height="36" viewBox="0 0 64 64" style={{ flexShrink: 0, display: 'block' }} aria-hidden="true">
            <rect width="64" height="64" rx="14" fill="#0d1b3e" stroke="rgba(212,175,55,0.28)" strokeWidth="1.5" />
            <g transform="rotate(-9 32 33)"><rect x="16" y="16" width="24" height="34" rx="4.5" fill="#a8863b" /></g>
            <g transform="rotate(8 32 33)"><rect x="24" y="15" width="24" height="34" rx="4.5" fill="#d4af37" /></g>
          </svg>
          {(!collapsed || isMobile) && (
            <div style={{ lineHeight: 1, whiteSpace: 'nowrap' }}>
              <div style={{ fontSize: 21, fontWeight: 900, letterSpacing: '0.01em', color: '#e2c069' }}>SCN</div>
              <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginTop: 3 }}>Sports Card Network</div>
            </div>
          )}
        </Link>
        {isMobile ? (
          <button className="sidebar-collapse-btn" onClick={() => setMobileOpen(false)} title="Close menu">
            <X size={16} />
          </button>
        ) : (
          <button
            className="sidebar-collapse-btn"
            onClick={() => { const np = !pinned; setPinned(np); setCollapsed(!np); }}
            title={pinned ? 'Unpin (auto-collapse)' : 'Pin open'}
          >
            {pinned ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
        )}
      </div>

      {/* Free-tier upgrade CTA (always visible while on free) */}
      {showUpgrade && (!collapsed || isMobile) && (
        <Link
          to="/settings?tab=subscription"
          className="sidebar-upgrade-cta"
          onClick={() => isMobile && setMobileOpen(false)}
        >
          <Sparkles size={16} strokeWidth={2.25} />
          <span>Upgrade to Paid</span>
        </Link>
      )}

      {/* Main Navigation */}
      <nav className="sidebar-nav">
        <div className="sidebar-section">
          {NAV_ITEMS.map(item => {
            // Team members without this permission don't see the page at all.
            if (item.requiredPermission && !can(item.requiredPermission)) return null;
            const Icon = item.icon;
            const active = isActive(item.path);
            const locked = item.requiredTier && unlockRank < TIER_RANK[item.requiredTier];
            // Orders badge = things needing action (orders to ship + pending eBay offers).
            const ordersCount = needsShipping + pendingOffers;
            const shipBadge = item.path === '/orders' && ordersCount > 0;
            // Grading gets the same count badge as Orders, "cards worth grading"
            // is a do-this-now signal, so it lives on the nav, not the dashboard.
            const gradeBadge = item.path === '/grading' && !locked && gradeCount > 0;
            const countBadge = shipBadge ? ordersCount : gradeBadge ? gradeCount : 0;
            const expanded = !collapsed || isMobile;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-tour={`sidebar-${item.tour}`}
                className={`sidebar-link ${active ? 'active' : ''}`}
                title={(collapsed && !isMobile) ? `${item.label}${shipBadge ? ` (${needsShipping} to ship)` : ''}${gradeBadge ? ` (${gradeCount} worth grading)` : ''}` : (locked ? `${item.requiredTier === 'starter' ? 'Starter' : 'Dealer'}+ feature` : undefined)}
              >
                <Icon size={21} strokeWidth={active ? 2 : 1.75} />
                {/* Collapsed rail: a dot on the icon so the count still signals. */}
                {!expanded && countBadge > 0 && <span className="sidebar-ship-dot" />}
                {expanded && <span className="sidebar-link-label">{item.label}</span>}
                {expanded && countBadge > 0 && <span className="sidebar-ship-badge">{countBadge > 99 ? '99+' : countBadge}</span>}
                {expanded && locked && (
                  <Lock size={13} strokeWidth={2.25} style={{ marginLeft: 'auto', color: '#c9a227', opacity: 0.75, flexShrink: 0 }} />
                )}
                {expanded && active && !locked && countBadge === 0 && <span className="sidebar-active-dot" />}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className="sidebar-bottom">
        {BOTTOM_ITEMS.map(item => {
          const Icon = item.icon;
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              data-tour={`sidebar-${item.tour}`}
              className={`sidebar-link ${active ? 'active' : ''}`}
              title={(collapsed && !isMobile) ? item.label : undefined}
            >
              <Icon size={21} strokeWidth={active ? 2 : 1.75} />
              {(!collapsed || isMobile) && <span className="sidebar-link-label">{item.label}</span>}
            </Link>
          );
        })}
        {/* Utility row, Report Bug / Book a Call / Get the App / What's New /
            Replay Tour compressed into ONE row of icon buttons (tooltips carry
            the labels). These used to be 5 pinned full-height rows that pushed
            real pages into a scroll. Hidden on the collapsed desktop rail -
            hover-expand reveals them. */}
        {(!collapsed || isMobile) && (
          <div className="sidebar-util-row">
            <Link to="/contact" className="sidebar-util-btn" title="Report a Bug" onClick={() => isMobile && setMobileOpen(false)}>
              <MessageSquare size={18} strokeWidth={1.75} />
            </Link>
            <a href={BOOK_CALL_URL} target="_blank" rel="noopener noreferrer" className="sidebar-util-btn" title="Book a Call" onClick={() => isMobile && setMobileOpen(false)}>
              <Phone size={18} strokeWidth={1.75} />
            </a>
            {!standalone && (
              <button onClick={handleGetApp} className="sidebar-util-btn" title="Get the App">
                <Download size={18} strokeWidth={1.75} />
              </button>
            )}
            <WhatsNew compact isMobile={isMobile} onOpenSomething={() => setMobileOpen(false)} />
            <button onClick={replayTour} className="sidebar-util-btn" title="Replay Tour">
              <HelpCircle size={18} strokeWidth={1.75} />
            </button>
          </div>
        )}

        {/* Account switcher (team members only, personal ↔ team) */}
        <AccountSwitcher expanded={!collapsed || isMobile} />

        {/* User Profile */}
        <div className="sidebar-user">
          <div className="sidebar-user-avatar">{initials}</div>
          {(!collapsed || isMobile) && (
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{displayName}</div>
              <button onClick={handleLogout} className="sidebar-logout-btn">Sign out</button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`sidebar ${collapsed ? 'sidebar-collapsed' : ''}`}
        onMouseEnter={() => { if (!pinned) setCollapsed(false); }}
        onMouseLeave={() => { if (!pinned) setCollapsed(true); }}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile top bar */}
      <div className="mobile-topbar">
        <button
          className="sidebar-mobile-btn"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
        >
          <Menu size={22} />
        </button>
        <span className="mobile-topbar-title">SportsCardNetwork</span>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile drawer */}
      <aside className={`sidebar sidebar-mobile-drawer ${mobileOpen ? 'sidebar-mobile-open' : ''}`}>
        {sidebarContent(true)}
      </aside>

      {/* Install instructions, for iOS Safari + browsers without a programmatic
          prompt (the native prompt path runs directly via handleGetApp). */}
      {showInstallHelp && (
        <div
          onClick={() => setShowInstallHelp(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, maxWidth: 380, width: '100%', padding: '22px', boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#0f172a' }}>Install SportsCardNetwork</div>
              <button onClick={() => setShowInstallHelp(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}><X size={18} /></button>
            </div>
            {isIos() ? (
              <ol style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 14, lineHeight: 1.8 }}>
                <li>Tap the <Share size={14} style={{ verticalAlign: '-2px' }} /> <strong>Share</strong> button in Safari's toolbar</li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong>, SCN lands on your home screen for one-tap scanning</li>
              </ol>
            ) : (
              <div style={{ color: '#334155', fontSize: 14, lineHeight: 1.7 }}>
                Open your browser menu (⋮) and choose <strong>Install app</strong> or <strong>Add to Home screen</strong>. On desktop Chrome, click the <Download size={14} style={{ verticalAlign: '-2px' }} /> install icon in the address bar.
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default Sidebar;
